import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";

import {
  ICreateLicenseRequestParams,
  ILicenseCalculateApproveParams,
  ILicenseRequestUpdateParams,
  IReviewLicenseRequestParams,
} from "~/entity/Licenses/model/schema";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { defaultErrorHandlerFunc } from "~/shared/hooks/useRequestHandler";

import { useLicenseRequests } from "./useLicenseRequests";

interface ReviewLicenseRequestVariables {
  id: string;
  data: IReviewLicenseRequestParams;
}

interface CreateLicenseRequestVariables {
  data: ICreateLicenseRequestParams;
  send?: boolean;
}

interface UpdateLicenseRequestVariables {
  id: string;
  data: ILicenseRequestUpdateParams;
  send?: boolean;
}

interface SendLicenseRequestVariables {
  id: string;
}

export const useSetLicenseRequest = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const { notifySuccess } = useNotify();
  const {
    queryKeys,
    transitionRequest,
    createRequest,
    updateRequest,
    approveCalculateRequest,
  } = useLicenseRequests();

  const invalidateRequests = () => {
    queryClient.invalidateQueries(queryKeys.all);
  };

  const createLicenseRequestMutation = useMutation(
    ({ data }: CreateLicenseRequestVariables) => createRequest(data),
    {
      onSuccess: (_response, { send }) => {
        invalidateRequests();
        notifySuccess(
          t(
            send
              ? "licenseManagement.notification.sendSuccess"
              : "licenseManagement.notification.createSuccess",
          ),
        );
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  const updateLicenseRequestMutation = useMutation(
    ({ id, data }: UpdateLicenseRequestVariables) => updateRequest(id, data),
    {
      onSuccess: (_response, { send }) => {
        invalidateRequests();
        notifySuccess(
          t(
            send
              ? "licenseManagement.notification.sendSuccess"
              : "licenseManagement.notification.createSuccess",
          ),
        );
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  const sendLicenseRequestMutation = useMutation(
    ({ id }: SendLicenseRequestVariables) =>
      transitionRequest(id, { action: "SEND" }),
    {
      onSuccess: () => {
        invalidateRequests();
        notifySuccess(t("licenseManagement.notification.sendSuccess"));
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  const approveLicenseRequestMutation = useMutation(
    ({ id, data }: ReviewLicenseRequestVariables) =>
      transitionRequest(id, {
        action: "APPROVE",
        comment: data.comment,
        items: data.items,
      }),
    {
      onSuccess: () => {
        invalidateRequests();
        notifySuccess(t("licenseManagement.notification.approveSuccess"));
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  const rejectLicenseRequestMutation = useMutation(
    ({ id, data }: ReviewLicenseRequestVariables) =>
      transitionRequest(id, { action: "REJECT", comment: data.comment }),
    {
      onSuccess: () => {
        invalidateRequests();
        notifySuccess(t("licenseManagement.notification.rejectSuccess"));
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  const approveAllCalculationMutation = useMutation(
    (data: ILicenseCalculateApproveParams) => approveCalculateRequest(data),
    {
      onSuccess: () => {
        invalidateRequests();
        notifySuccess(t("licenseManagement.notification.approveAllSuccess"));
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  return {
    createLicenseRequest: createLicenseRequestMutation,
    updateLicenseRequest: updateLicenseRequestMutation,
    sendLicenseRequest: sendLicenseRequestMutation,
    approveLicenseRequest: approveLicenseRequestMutation,
    rejectLicenseRequest: rejectLicenseRequestMutation,
    approveAllCalculation: approveAllCalculationMutation,
  };
};
